import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Renderer,
  DataProvider,
  VisibilityProvider,
  ActionProvider,
} from '@json-render/react';

import { orpc } from '@/api/rpc/client';
import { portalComponentRegistry } from '@/components/portal/registry';
import { ForegroundAgentChat } from '@/components/common/ForegroundAgentChat';
import Page from '@/components/page/Page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useToast } from '@/context/ToastContext';
import type { PortalStatus, PortalUserStatus, PortalVersion } from '@erp/rpc';

const EMPTY_VERSIONS: PortalVersion[] = [];

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

function formatDate(date: Date) {
  return dateFormatter.format(date);
}

function statusLabel(status: PortalStatus) {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'published':
      return 'Published';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function userStatusLabel(status: PortalUserStatus) {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'active':
      return 'Active';
    case 'deactivated':
      return 'Deactivated';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function userStatusVariant(status: PortalUserStatus) {
  switch (status) {
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'deactivated':
      return 'secondary';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Unknown error';
}

function sortVersionsDesc(a: PortalVersion, b: PortalVersion) {
  return b.createdAt.getTime() - a.createdAt.getTime();
}

export function PortalDetailPage() {
  const navigate = useNavigate();
  const { open: openAgentPanel } = useAgentPanel();
  const { showToast } = useToast();
  const { portalId } = useParams({ from: '/authenticated/portals/$portalId' });

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [deactivatingPortalUserId, setDeactivatingPortalUserId] = useState<
    string | null
  >(null);

  const currentPage = `/portals/${portalId}`;

  const portalQuery = useQuery(
    orpc.portal.get.queryOptions({ input: { portalId } }),
  );

  const portal = portalQuery.data?.portal ?? null;
  const versions = portalQuery.data?.versions ?? EMPTY_VERSIONS;

  const portalUsersQuery = useQuery({
    ...orpc.portal.listUsers.queryOptions({ input: { portalId } }),
    enabled: Boolean(portal),
  });

  const previewInput = useMemo(
    () => ({
      portalId,
      versionId: portal?.currentVersionId ?? undefined,
    }),
    [portalId, portal?.currentVersionId],
  );

  const previewQuery = useQuery({
    ...orpc.portal.getPreview.queryOptions({ input: previewInput }),
    enabled: Boolean(portal),
  });

  const publishMutation = useMutation(
    orpc.portal.publish.mutationOptions({
      onSuccess: async () => {
        await Promise.all([portalQuery.refetch(), previewQuery.refetch()]);
        showToast({
          title: 'Portal published',
          message: portal?.name,
          color: 'success',
          icon: 'check',
        });
      },
      onError: (error) => {
        showToast({
          title: 'Publish failed',
          message: getErrorMessage(error),
          color: 'error',
          icon: 'warning',
        });
      },
    }),
  );

  const unpublishMutation = useMutation(
    orpc.portal.unpublish.mutationOptions({
      onSuccess: async () => {
        await Promise.all([portalQuery.refetch(), previewQuery.refetch()]);
        showToast({
          title: 'Portal unpublished',
          message: portal?.name,
          color: 'success',
          icon: 'check',
        });
      },
      onError: (error) => {
        showToast({
          title: 'Unpublish failed',
          message: getErrorMessage(error),
          color: 'error',
          icon: 'warning',
        });
      },
    }),
  );

  const rollbackMutation = useMutation(
    orpc.portal.rollback.mutationOptions({
      onSuccess: async () => {
        await Promise.all([portalQuery.refetch(), previewQuery.refetch()]);
        showToast({
          title: 'Portal rolled back',
          message: portal?.name,
          color: 'success',
          icon: 'check',
        });
      },
      onError: (error) => {
        showToast({
          title: 'Rollback failed',
          message: getErrorMessage(error),
          color: 'error',
          icon: 'warning',
        });
      },
    }),
  );

  const inviteUserMutation = useMutation(
    orpc.portal.inviteUser.mutationOptions({
      onSuccess: async (data) => {
        setInviteUrl(data.inviteUrl);
        await portalUsersQuery.refetch();
        showToast({
          title: 'Invite created',
          message: 'Share the invite link with the user.',
          color: 'success',
          icon: 'check',
        });
      },
      onError: (error) => {
        showToast({
          title: 'Invite failed',
          message: getErrorMessage(error),
          color: 'error',
          icon: 'warning',
        });
      },
    }),
  );

  const deactivateUserMutation = useMutation(
    orpc.portal.deactivateUser.mutationOptions({
      onSuccess: async () => {
        await portalUsersQuery.refetch();
        showToast({
          title: 'User deactivated',
          color: 'success',
          icon: 'check',
        });
      },
      onError: (error) => {
        showToast({
          title: 'Deactivation failed',
          message: getErrorMessage(error),
          color: 'error',
          icon: 'warning',
        });
      },
    }),
  );

  const regenerateMutation = useMutation(
    orpc.portal.regenerateAsJson.mutationOptions({
      onSuccess: async () => {
        await portalQuery.refetch();
        showToast({
          title: 'Portal regenerated',
          message: 'Converted to JSON format',
          color: 'success',
          icon: 'check',
        });
      },
      onError: (error) => {
        showToast({
          title: 'Regeneration failed',
          message: getErrorMessage(error),
          color: 'error',
          icon: 'warning',
        });
      },
    }),
  );

  const sortedVersions = useMemo(
    () => [...versions].sort(sortVersionsDesc),
    [versions],
  );

  const preview = previewQuery.data;
  const isMutating =
    publishMutation.isPending ||
    unpublishMutation.isPending ||
    rollbackMutation.isPending;

  const currentVersion = versions.find(
    (v) => v.id === portal?.currentVersionId,
  );
  const showRegenerateButton =
    currentVersion && currentVersion.html && !currentVersion.jsonTree;

  function handleEditWithAi() {
    openAgentPanel();
  }

  async function handlePublish() {
    if (!portal) return;
    await publishMutation.mutateAsync({ portalId: portal.id });
  }

  async function handleUnpublish() {
    if (!portal) return;
    await unpublishMutation.mutateAsync({ portalId: portal.id });
  }

  async function handleRollback(versionId: string) {
    if (!portal) return;
    const confirmed = window.confirm(
      'Rollback this portal to the selected version?',
    );
    if (!confirmed) return;
    await rollbackMutation.mutateAsync({ portalId: portal.id, versionId });
  }

  async function handleInviteUser() {
    if (!portal) return;
    const email = inviteEmail.trim();
    const name = inviteName.trim();
    if (!email || !name) return;
    await inviteUserMutation.mutateAsync({
      customerId: portal.customerId,
      email,
      name,
    });
  }

  async function handleCopyInviteUrl() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      showToast({
        title: 'Copied',
        message: 'Invite URL copied to clipboard.',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Copy failed',
        message: getErrorMessage(error),
        color: 'error',
        icon: 'warning',
      });
    }
  }

  async function handleDeactivateUser(portalUserId: string) {
    if (!portal) return;
    const confirmed = window.confirm('Deactivate this portal user?');
    if (!confirmed) return;

    setDeactivatingPortalUserId(portalUserId);
    try {
      await deactivateUserMutation.mutateAsync({
        portalId: portal.id,
        portalUserId,
      });
    } finally {
      setDeactivatingPortalUserId(null);
    }
  }

  const actions = (
    <>
      <Button variant="secondary" onClick={handleEditWithAi} disabled={!portal}>
        Edit with AI
      </Button>
      {showRegenerateButton && (
        <Button
          variant="secondary"
          onClick={() => regenerateMutation.mutateAsync({ portalId })}
          disabled={regenerateMutation.isPending}
        >
          {regenerateMutation.isPending
            ? 'Regenerating...'
            : 'Regenerate as JSON'}
        </Button>
      )}
      {portal?.status === 'published' ? (
        <Button
          variant="secondary"
          onClick={handleUnpublish}
          disabled={!portal || isMutating}
        >
          {unpublishMutation.isPending ? 'Unpublishing...' : 'Unpublish'}
        </Button>
      ) : (
        <Button onClick={handlePublish} disabled={!portal || isMutating}>
          {publishMutation.isPending ? 'Publishing...' : 'Publish'}
        </Button>
      )}
    </>
  );

  return (
    <>
      <Page
        title={portal?.name ?? 'Portal'}
        subtitle={portal?.customer?.name ?? undefined}
        isLoading={portalQuery.isLoading}
        error={portalQuery.error ?? undefined}
        actions={actions}
      >
        {!portal ? (
          <div className="mt-3 rounded-md border bg-background p-4">
            <p className="text-sm text-muted-foreground">Portal not found</p>
            <p className="mt-2 font-mono text-sm">{portalId}</p>
          </div>
        ) : (
          <Tabs defaultValue="info" className="mt-3">
            <TabsList>
              <TabsTrigger value="info">Portal Info</TabsTrigger>
              <TabsTrigger value="users">Portal Users</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="pt-3">
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle>Portal Info</CardTitle>
                    <CardAction>
                      <Badge
                        variant={
                          portal.status === 'published' ? 'success' : 'warning'
                        }
                      >
                        {statusLabel(portal.status)}
                      </Badge>
                    </CardAction>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="text-sm font-medium">{portal.name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Customer
                        </p>
                        <p className="text-sm font-medium">
                          {portal.customer?.name ?? '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Created</p>
                        <p className="text-sm font-medium">
                          {formatDate(portal.createdAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Portal ID
                        </p>
                        <p className="font-mono text-sm">{portal.id}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid gap-3 lg:grid-cols-3">
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle>Preview</CardTitle>
                      <CardAction>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => void previewQuery.refetch()}
                          disabled={previewQuery.isFetching || !portal}
                        >
                          {previewQuery.isFetching
                            ? 'Refreshing...'
                            : 'Refresh'}
                        </Button>
                      </CardAction>
                    </CardHeader>
                    <CardContent>
                      {previewQuery.isLoading ? (
                        <div className="h-96 rounded-md border bg-muted" />
                      ) : preview?.jsonTree && preview.sampleData ? (
                        <DataProvider initialData={preview.sampleData}>
                          <VisibilityProvider>
                            <ActionProvider>
                              <div className="w-full h-96 overflow-auto border rounded-md p-4 bg-gray-50">
                                <Renderer
                                  tree={preview.jsonTree as any}
                                  registry={portalComponentRegistry}
                                />
                              </div>
                            </ActionProvider>
                          </VisibilityProvider>
                        </DataProvider>
                      ) : preview?.html ? (
                        <iframe
                          srcDoc={preview.html}
                          className="w-full h-96 border-0"
                          title="Portal Preview"
                        />
                      ) : (
                        <div className="rounded-md border bg-background p-3">
                          <p className="text-sm text-muted-foreground">
                            No preview available.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Version History</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {sortedVersions.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No versions yet.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {sortedVersions.map((version) => {
                            const isCurrent =
                              portal.currentVersionId === version.id;
                            return (
                              <div
                                key={version.id}
                                className="flex items-center justify-between gap-3 rounded-md border bg-background p-2"
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium">
                                    {formatDate(version.createdAt)}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {version.id}
                                  </p>
                                </div>

                                <div className="flex items-center gap-2">
                                  {isCurrent ? (
                                    <Badge variant="secondary">Current</Badge>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={() =>
                                        void handleRollback(version.id)
                                      }
                                      disabled={isMutating}
                                    >
                                      Rollback
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="users" className="pt-3">
              <div className="space-y-3">
                <Card>
                  <CardHeader>
                    <CardTitle>Portal Users</CardTitle>
                    <CardAction>
                      <Button
                        onClick={() => {
                          setInviteDialogOpen(true);
                          setInviteUrl(null);
                          setInviteEmail('');
                          setInviteName('');
                        }}
                      >
                        Invite User
                      </Button>
                    </CardAction>
                  </CardHeader>
                  <CardContent>
                    {portalUsersQuery.isLoading ? (
                      <div className="h-24 rounded-md border bg-muted" />
                    ) : portalUsersQuery.error ? (
                      <div className="rounded-md border bg-background p-3">
                        <p className="text-sm text-muted-foreground">
                          Failed to load portal users.
                        </p>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {getErrorMessage(portalUsersQuery.error)}
                        </p>
                      </div>
                    ) : portalUsersQuery.data?.users.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Last Login</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead className="w-24" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {portalUsersQuery.data.users.map((user) => {
                            const isDeactivating =
                              deactivatingPortalUserId === user.id;
                            return (
                              <TableRow key={user.id}>
                                <TableCell className="font-mono text-xs">
                                  {user.email}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {user.name}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant={userStatusVariant(user.status)}
                                  >
                                    {userStatusLabel(user.status)}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {user.lastLoginAt
                                    ? formatDate(user.lastLoginAt)
                                    : '-'}
                                </TableCell>
                                <TableCell>
                                  {formatDate(user.createdAt)}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() =>
                                      void handleDeactivateUser(user.id)
                                    }
                                    disabled={
                                      user.status === 'deactivated' ||
                                      deactivateUserMutation.isPending ||
                                      isDeactivating
                                    }
                                  >
                                    {isDeactivating
                                      ? 'Deactivating...'
                                      : 'Deactivate'}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No users yet.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </Page>

      <Dialog
        open={inviteDialogOpen}
        onOpenChange={(open) => {
          setInviteDialogOpen(open);
          if (!open) setInviteUrl(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite user</DialogTitle>
            <DialogDescription>
              Create an invite link for a customer portal user.
            </DialogDescription>
          </DialogHeader>

          {inviteUrl ? (
            <div className="space-y-2">
              <p className="text-sm font-medium">Invite URL</p>
              <div className="flex items-center gap-2">
                <Input
                  value={inviteUrl}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button
                  variant="secondary"
                  onClick={() => void handleCopyInviteUrl()}
                >
                  Copy
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Anyone with this link can complete the signup.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Email</p>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder="user@example.com"
                />
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Name</p>
                <Input
                  value={inviteName}
                  onChange={(event) => setInviteName(event.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setInviteDialogOpen(false)}
            >
              {inviteUrl ? 'Close' : 'Cancel'}
            </Button>
            {!inviteUrl && (
              <Button
                onClick={() => void handleInviteUser()}
                disabled={
                  inviteUserMutation.isPending ||
                  !inviteEmail.trim() ||
                  !inviteName.trim()
                }
              >
                {inviteUserMutation.isPending ? 'Inviting...' : 'Create invite'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ForegroundAgentChat
        moduleKey="portal"
        currentPage={currentPage}
        hideFloatingButton
        onNavigate={(path, options) =>
          navigate({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to: path as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            search: options?.search as any,
          })
        }
        onPortalUpdated={() => {
          void portalQuery.refetch();
          void previewQuery.refetch();
        }}
      />
    </>
  );
}
